<template>
  <section class="form-fw__content">
    <form
      v-if="status === isSuccess"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <FormBox
        :loading="status === isLoading"
        :title="routeName === 'CreateCompany' ? $t(`${namespace}.financialConfig`) : $t(`${namespace}.general`)"
        :icon="require('@/assets/icons/icn_money-paper-accent.svg')"
      >
        <div class="cursor--not-allowed">
          <v-layout row wrap>
            <v-flex sm6 xs12>
              <div class="pr-2--desktop">
                <label>{{ $t(`${namespace}.fieldPeriodicityConfigTypeLabel`) }}</label>
                <v-select
                  v-model="form.periodicityConfigType"
                  :placeholder="$t(`${namespace}.fieldPeriodicityConfigTypePlaceholder`)"
                  item-text="label"
                  :items="objectToArray($t(`${namespace}.fieldPeriodicityConfigTypeOptions`))"
                  :error-messages="errors.collect($t(`${namespace}.fieldPeriodicityConfigTypeName`))"
                  v-validate="{ required: true }"
                  :data-vv-name="$t(`${namespace}.fieldPeriodicityConfigTypeName`)"
                  noDataText=""
                  disabled
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>

            <v-flex sm6 xs12>
              <div class="pl-2--desktop">
                <div
                  v-if="form.periodicityConfigType === 'MONTHLY'"
                  v-html="$t(`${namespace}.monthlyPeriodDisclaimer`)"
                  class="period__disclaimer"
                />

                <div
                  v-if="form.periodicityConfigType === 'MANY_DAYS'"
                  v-html="$t(`${namespace}.biweeklyPeriodDisclaimer`)"
                  class="period__disclaimer"
                />

                <div
                  v-if="form.periodicityConfigType === 'DAILY'"
                  v-html="$t(`${namespace}.dailyPeriodDisclaimer`)"
                  class="period__disclaimer"
                />
              </div>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex sm6 xs12>
              <div class="pr-2--desktop">
                <label>{{ $t(`${namespace}.fieldPaymentMethodLabel`) }}</label>
                <div class="input__tooltip">
                  <v-select
                    v-model="form.paymentMethod"
                    :placeholder="$t(`${namespace}.fieldPaymentMethodPlaceholder`)"
                    item-text="label"
                    :items="fieldPaymentMethodOptions"
                    :disabledItems="['CREDIT_CARD']"
                    :error-messages="errors.collect($t(`${namespace}.fieldPaymentMethodName`))"
                    v-validate="{ required: true }"
                    :data-vv-name="$t(`${namespace}.fieldPaymentMethodName`)"
                    noDataText=""
                    disabled
                    required
                    flat
                    outline
                    solo
                    color="accent"
                  />

                  <v-tooltip min-width="204" max-width="204" top>
                    <v-icon color="accent" slot="activator">info</v-icon>
                    <span>{{ $t(`${namespace}.paymentMethodTooltip`) }}</span>
                  </v-tooltip>
                </div>
              </div>
            </v-flex>

            <v-flex sm6 xs12>
              <div class="pl-2--desktop">
                <label>{{ $t(`${namespace}.fieldDueDaysLabel`) }}</label>
                <v-text-field
                  v-model="form.dueDays"
                  type="number"
                  :placeholder="$t(`${namespace}.fieldDueDaysPlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldDueDaysName`))"
                  v-validate="{ required: true, integer: true }"
                  :data-vv-name="$t(`${namespace}.fieldDueDaysName`)"
                  disabled
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>
          </v-layout>
        </div>

        <label>
          {{ $t(`${namespace}.fieldEmailsToNotifyLabel`) }}
          <small>({{ $t('Common.emailComboboxHint') }})</small>
        </label>
        <v-combobox
          v-model="form.emailsToNotify"
          :placeholder="$t(`${namespace}.fieldEmailsToNotifyPlaceholder`)"
          :error-messages="errors.collect($t(`${namespace}.fieldEmailsToNotifyName`))"
          v-validate="{ required: true, emailList: true }"
          :data-vv-name="$t(`${namespace}.fieldEmailsToNotifyName`)"
          noDataText=""
          multiple
          chips
          required
          flat
          outline
          solo
          color="accent"
        >
          <template v-slot:selection="{ item, parent, selected }">
            <v-chip
              :selected="selected"
              :class="[
                { 'error': !emailRegex.test(item) },
                { 'theme--dark': !emailRegex.test(item) },
              ]"
              close
              @input="parent.selectItem(item)"
            >
              {{ item }}
            </v-chip>
          </template>
        </v-combobox>
      </FormBox>
    </form>

    <div
      v-if="status === isFailed"
      class="text-xs-center mt-5"
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="tryAgain"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import { VMoney } from 'v-money'
import { isObject, isArray, objectToArray, goBack } from '@/utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import FormBox from 'components/FormBox'

export default {
  name: 'CostCenterForm',
  props: {
    data: {
      type: Object
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  components: {
    FormBox
  },
  directives: {
    money: VMoney
  },
  computed: {
    isLoading: () => STATUS_IS_LOADING,

    isSuccess: () => STATUS_SUCCESS,

    isFailed: () => STATUS_FAILED,

    routeName () {
      return this.$route.name
    },

    companyId () {
      return this.$route.params && this.$route.params.id
        ? this.$route.params.id
        : null
    },

    fieldPaymentMethodOptions () {
      const options = objectToArray(this.$t(`${this.namespace}.fieldPaymentMethodOptions`))
      return this.form.periodicityConfigType === 'DAILY'
        ? options.filter(opt => opt.value === 'CREDIT_CARD')
        : options
    }
  },
  data () {
    return {
      namespace: 'Views.CompanyFinancialConfigPage',
      isFetching: false,
      emailRegex: /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/,
      userMessageBackup: 'user message backup',
      employeeMessageBackup: 'employee message backup',

      // mask money config
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },

      // form fields
      form: {}
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    tryAgain () {
      if (this.routeName === 'CompanyFinancialConfig') {
        this.$emit('tryAgain')
        return
      }

      location.reload()
    },

    isValidObject: (val) => isObject(val),

    isValidArray: (val) => isArray(val),

    objectToArray: (val) => objectToArray(val),

    prevPage () {
      goBack()
    },

    renderPayload () {
      if (this.isValidObject(this.data)) {
        const {
          periodicityConfigType,
          paymentMethod,
          dueDays,
          emailsToNotify
        } = this.data

        this.form = {
          periodicityConfigType,
          paymentMethod,
          dueDays,
          emailsToNotify
        }
      }
    },

    restoreMessage (field) {
      if (field === 'userMessage') this.form.userMessage = this.userMessageBackup
      if (field === 'employeeMessage') this.form.employeeMessage = this.employeeMessageBackup
    },

    resetAllFields () {
      this.form.periodicityConfigType = null
      this.form.paymentMethod = null
      this.form.dueDays = null
      this.form.emailsToNotify = []
    },

    async getCompany () {
      try {
        const companyId = this.companyId
        const { data: company } = await this.$apiService.getById(companyId)
        const { name: companyName, documentId } = company.data

        if (!company || !this.isValidObject(company)) {
          throw new Error()
        }

        this.form.companyId = companyId
        this.form.companyName = companyName
        this.form.documentId = documentId
      } catch (err) {
        console.log('ERROR:', err)
        this.$emit('setCurrentStatus', this.isFailed)
      }
    },

    async edit () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const { financialAccountId } = this.data
        const { emailsToNotify } = this.form
        const { data: res } = await this.$apiService.billing.updateEmailsToNotify(financialAccountId, { emailsToNotify })

        if (res && !res.valid) {
          const msg = this.isValidArray(res.reasons) ? res.reasons[0] : null
          this.showAlert({
            type: 'error',
            message: msg
          })
        } else {
          this.$router.push({ name: 'Company' }).catch(() => {})
          this.showAlert({
            type: 'success',
            message: this.$t(`${this.namespace}.editSuccessMessage`)
          })
        }

        scrollTo(0, 0)
        this.$emit('setCurrentStatus', this.isSuccess)
        // this.$emit('tryAgain', this.isSuccess)
      } catch (err) {
        console.log('ERROR', err)
        this.$emit('setCurrentStatus', this.isSuccess)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.editErrorMessage`)
        })
      }
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()
        // TO DO
        // this.dispatchHeaderErrorMessage()

        if (isValid) {
          this.edit()
        } else {
          const target = this.$validator.errors.items[0]
          if (target) {
            const elem = this.$el.querySelector(`[data-vv-name="${target.field}"]`)
            elem.scrollIntoView(false)
            elem.focus()
          }
        }
      } catch (err) {
        console.log('ERROR', err)
      }
    }
  },
  watch: {
    submit () {
      this.handleSubmit()
    },

    data () {
      this.renderPayload()
    }
  },
  created () {
    this.$validator.extend('emailList', {
      getMessage: field => this.$t(`${this.namespace}.emailErrorMessage`),
      validate: value => {
        const validate = value.find(email => !this.emailRegex.test(email))
        return !validate
      }
    })
  },
  mounted () {
    this.isFetching = true
    this.renderPayload()
  }
}
</script>

<style lang="scss" scoped>
.period__disclaimer {
  background-color: $gray-2;
  font-size: 10px;
  margin-bottom: 24px;
  min-height: 48px;
  padding: 9px 16px;
  position: relative;

  @media (min-width: 992px) {
    margin-bottom: 0;
    margin-top: 24px;
  }

  &:before {
    content: '';
    display: none;
    border-bottom: 6px solid transparent;
    border-left: 6px solid transparent;
    border-right: 6px solid $gray-2;
    border-top: 6px solid transparent;
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);

    @media (min-width: 992px) {
      display: inline-block;
    }
  }
}

.textarea__restore {
  position: relative;

  .v-btn {
    margin: 0;
    position: absolute;
    right: 4px;
    top: 4px;
    z-index: 1;
  }
}

.input--padding-left {
  padding-left: 32px;
}
</style>

<style lang="scss">
.v-chip.theme--light.theme--dark {

  > .v-chip__content > .v-chip__close > .v-icon {
    color: #fff !important;
  }
}
</style>
